import { Link } from 'react-router-dom';

import { formatearImporte } from '../../../nucleo/importes/formatoDeImporte.js';
import { SIN_CIFRA, bloquesDelResumen, esNegativo } from '../logica/agregacion.js';
import {
  ESTADO_DE_TARJETA, leerTotalesDelMes, resolverEstadoDeTarjeta,
} from '../logica/estadosHonestos.js';
import { TarjetaCargando, TarjetaConError } from './EstadoDeTarjeta.jsx';

/*
 * T-1 · RESUMEN DEL MES (RF-211, RN-601 a RN-604).
 *
 * UN BLOQUE POR MONEDA con tres cifras —Ingresos, Gastos y Diferencia— y los
 * bloques NUNCA se combinan (RN-602, RN-003, RN-033). Una moneda sin movimientos
 * en el mes no muestra bloque.
 *
 * TODO LLEGA RESUELTO de `totalesPorMoneda` de ConsultarMovimientos (RN-601):
 *   · los cancelados no suman, aunque existan (D-42, D-48);
 *   · los eliminados no existen para ninguna consulta (12/16);
 *   · los movimientos de cuentas desactivadas SÍ cuentan: son historial del mes y
 *     el listado los incluye (la exclusión de desactivadas es de SALDOS, RN-611);
 *   · de una compra a meses cuenta SOLO la mensualidad imputada al mes (D-311);
 *   · los gastos van netos de devoluciones (RN-095);
 *   · los traspasos y los pagos de tarjeta no son ingreso ni gasto (RN-083).
 *
 * RN-604 · MES SIN NINGÚN MOVIMIENTO: la tarjeta NO se oculta. Es la que define al
 * Tablero y su ausencia total dejaría la pantalla sin ancla; muestra su mensaje y
 * el acceso a capturar. Contraste deliberado con T-4, que sí desaparece (RN-610).
 */
export function TarjetaDelResumenDelMes({ nombreDelMes, movimientos, alCapturar }) {
  const TITULO = 'Resumen del mes';

  /* FIX del 2026-10-08 · los cuatro estados los resuelve la regla general
     (RN-622), no un `if` propio de esta tarjeta. Lo que gana con el cambio es la
     pregunta que antes nadie hacía: una 200 cuya forma no corresponde al contrato
     ya NO se lee como «mes sin movimientos», se lee como error (RN-622b). */
  const resumen = resolverEstadoDeTarjeta(movimientos, leerTotalesDelMes);

  if (resumen.estado === ESTADO_DE_TARJETA.ERROR) {
    return (
      <TarjetaConError
        titulo={TITULO}
        error={movimientos.error}
        mensaje={resumen.ilegible ? `No se pudo cargar tu resumen de ${nombreDelMes}.` : null}
        alReintentar={movimientos.recargar}
        dato="resumen-del-mes-con-error"
      />
    );
  }

  if (resumen.estado === ESTADO_DE_TARJETA.CARGANDO) {
    return <TarjetaCargando titulo={TITULO} renglones={3} dato="resumen-del-mes-cargando" />;
  }

  /* RN-604 · el mes en blanco, con su acceso a capturar. ÚNICO estado donde este
     texto puede aparecer (RN-623). */
  if (resumen.estado === ESTADO_DE_TARJETA.VACIO) {
    return (
      <section aria-label={TITULO} data-prueba="resumen-del-mes">
        <div className="gtit">{TITULO}</div>
        <div className="card">
          <div className="tvacio">
            <b data-prueba="mes-sin-movimientos">Aún no hay movimientos en {nombreDelMes}</b>
            Captura el primero y aquí verás tus ingresos, tus gastos y la diferencia.
          </div>
          <button className="btn ancho" type="button" onClick={alCapturar} data-prueba="capturar-desde-el-tablero">
            Capturar un movimiento
          </button>
        </div>
      </section>
    );
  }

  const bloques = bloquesDelResumen(movimientos.datos);

  return (
    <section aria-label={TITULO} data-prueba="resumen-del-mes">
      <div className="gtit">{TITULO}</div>
      <div className="card">
        {bloques.map((bloque) => (
          <div className="mon" key={bloque.idMoneda} data-prueba="bloque-de-moneda" data-moneda={bloque.codigoISO}>
            <h4>{bloque.rotulo}</h4>
            <div className="tri">
              <div>
                <small>Ingresos</small>
                {/* RN-603 · un tipo sin movimientos se pinta «—», no «$0.00». */}
                <b className={bloque.ingresos === null ? undefined : 'i'} data-prueba="ingresos-del-mes">
                  {bloque.ingresos === null ? SIN_CIFRA : formatearImporte(bloque.ingresos, bloque.codigoISO)}
                </b>
              </div>
              <div>
                <small>Gastos</small>
                <b className={bloque.gastos === null ? undefined : 'g'} data-prueba="gastos-del-mes">
                  {bloque.gastos === null ? SIN_CIFRA : formatearImporte(bloque.gastos, bloque.codigoISO)}
                </b>
              </div>
              <div>
                <small>Diferencia</small>
                {/* La resta se hace UNA vez, en centavos enteros y sobre los
                    decimales exactos del servidor (RN-603). */}
                <b className={esNegativo(bloque.diferencia) ? 'g' : undefined} data-prueba="diferencia-del-mes">
                  {formatearImporte(bloque.diferencia, bloque.codigoISO)}
                </b>
              </div>
            </div>
          </div>
        ))}

        <div className="tpie">
          <span className="crece">
            Los traspasos y los pagos de tarjeta no son ingreso ni gasto, así que no cuentan. Los
            cancelados tampoco, y las devoluciones restan del gasto.
          </span>
          <Link className="btn sec" to="/movimientos">Ver movimientos</Link>
        </div>
      </div>
    </section>
  );
}
